import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles,
  Container,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  Link,
  InputAdornment
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import GetAppIcon from "@material-ui/icons/GetApp";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import StorageOutlinedIcon from "@material-ui/icons/StorageOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import AccountBalanceOutlinedIcon from "@material-ui/icons/AccountBalanceOutlined";
import HistoryIcon from "@material-ui/icons/History";
import LinkIcon from "@material-ui/icons/Link";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import PhoneIphoneIcon from "@material-ui/icons/PhoneIphone";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  VERDE,
  bannerNavy,
  bannerTitulo,
  bannerSub,
  botaoVidro,
  modalPapel,
  modalHero,
  modalHeroEtiqueta,
  modalHeroTitulo,
  modalHeroChip,
  modalFechar,
  modalCorpo,
  modalSecao,
  modalSecaoTopo,
  modalSecaoIcone,
  modalSecaoTitulo,
  modalRodape,
  campoNavy,
  botaoPrimario,
  botaoSecundario,
  botaoPerigo,
  botaoPerigoCheio
} from "../../utils/estiloNavy";

const POR_PAGINA = 20;

const useStyles = makeStyles(theme => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(148,163,184,0.12)" : "#edf1f5";
  const painel = {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${escuro ? "rgba(255,255,255,0.06)" : "#eef2f6"}`,
    borderRadius: 18,
    boxShadow: escuro ? "0 6px 20px rgba(0,0,0,.3)" : "0 4px 16px rgba(15,23,42,.05)"
  };
  return {
    root: {
      padding: theme.spacing(3),
      height: "100%",
      overflowY: "auto",
      backgroundColor: "transparent"
    },
    // ── Banner ──
    banner: {
      ...bannerNavy(theme),
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      flexWrap: "wrap",
      marginBottom: theme.spacing(2.5)
    },
    bannerTitulo,
    bannerSub,
    bannerIcone: {
      width: 40,
      height: 40,
      borderRadius: 12,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg,#22c55e,#15803d)",
      boxShadow: "0 6px 18px rgba(34,197,94,.35)"
    },
    bannerDireita: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    totalPilula: {
      padding: "8px 14px",
      borderRadius: 14,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.12)",
      color: "#fff",
      textAlign: "right",
      lineHeight: 1.1
    },
    totalNumero: { fontSize: "1.25rem", fontWeight: 800 },
    totalRotulo: {
      fontSize: "0.66rem",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#94a3b8"
    },
    botaoVidro,

    // ── Filtros ──
    filtros: {
      ...painel,
      padding: theme.spacing(1.75, 2),
      marginBottom: theme.spacing(2),
      display: "flex",
      gap: theme.spacing(1.25),
      flexWrap: "wrap",
      alignItems: "center",
      animation: "iaSubir .45s ease backwards"
    },
    campo: { ...campoNavy(theme), minWidth: 160, flex: "1 1 160px" },
    btnBuscar: { ...botaoPrimario, height: 40 },
    btnLimpar: { ...botaoSecundario(theme), height: 40 },

    // ── Lista ──
    listaPainel: { ...painel, overflow: "hidden", animation: "iaSubir .5s ease backwards" },
    lista: { display: "flex", flexDirection: "column", gap: 8, padding: 12 },
    linha: {
      display: "grid",
      gridTemplateColumns:
        "minmax(240px, 2.3fr) minmax(130px, 1fr) minmax(170px, 1.3fr) minmax(130px, 1fr) minmax(130px, 1fr) 28px",
      alignItems: "center",
      gap: 14,
      padding: "10px 14px",
      "& > *": { minWidth: 0 },
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "minmax(220px, 2fr) minmax(160px, 1.3fr) minmax(130px, 1fr) 28px",
        "& > :nth-child(2), & > :nth-child(5)": { display: "none" }
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr",
        "& > :nth-child(3), & > :nth-child(4), & > :nth-child(6)": { display: "none" }
      }
    },
    cabecalho: {
      position: "sticky",
      top: 0,
      zIndex: 2,
      padding: "8px 14px",
      fontSize: "0.68rem",
      fontWeight: 800,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      background: escuro ? "rgba(15,23,42,0.92)" : "rgba(248,250,252,0.95)",
      borderRadius: 10
    },
    cartao: {
      position: "relative",
      cursor: "pointer",
      borderRadius: 14,
      border: `1px solid ${borda}`,
      background: escuro ? "rgba(255,255,255,0.02)" : "#fff",
      transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
      animation: "iaSubir .35s ease backwards",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 10,
        bottom: 10,
        width: 3,
        borderRadius: 3,
        background: "#22c55e",
        opacity: 0,
        transition: "opacity .18s ease"
      },
      "&:hover": {
        transform: "translateY(-1px)",
        borderColor: "rgba(34,197,94,0.35)",
        boxShadow: escuro ? "0 10px 24px rgba(0,0,0,.4)" : "0 10px 22px rgba(15,23,42,.07)",
        "&::before": { opacity: 1 },
        "& $seta": { color: VERDE, transform: "translateX(2px)" }
      }
    },
    pessoa: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
    avatar: {
      width: 38,
      height: 38,
      borderRadius: 12,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 800,
      fontSize: "0.85rem",
      color: VERDE,
      background: escuro ? "rgba(30,152,85,0.16)" : "rgba(30,152,85,0.10)"
    },
    nome: {
      fontSize: "0.88rem",
      fontWeight: 700,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textTransform: "capitalize"
    },
    sub: {
      fontSize: "0.76rem",
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    forte: { fontSize: "0.86rem", fontWeight: 700, color: theme.palette.text.primary, fontVariantNumeric: "tabular-nums" },
    etiquetaProduto: {
      display: "inline-block",
      fontSize: "0.68rem",
      fontWeight: 800,
      letterSpacing: "0.04em",
      padding: "2px 8px",
      borderRadius: 6,
      marginRight: 6,
      color: "#2563eb",
      background: "rgba(37,99,235,0.10)"
    },
    status: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: "0.72rem",
      fontWeight: 700,
      padding: "3px 10px",
      borderRadius: 999,
      whiteSpace: "nowrap",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },
    ponto: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },
    seta: { color: theme.palette.text.disabled, transition: "transform .18s, color .18s" },
    vazio: {
      padding: theme.spacing(8, 2),
      textAlign: "center",
      color: theme.palette.text.secondary
    },
    rodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: theme.spacing(1.25, 2),
      borderTop: `1px solid ${borda}`,
      flexWrap: "wrap",
      gap: theme.spacing(1)
    },
    paginaBtn: {
      borderRadius: 10,
      border: `1px solid ${borda}`,
      "&:hover": { borderColor: VERDE, color: VERDE }
    },

    // ── Modal ──
    papel: modalPapel(theme, 880),
    hero: modalHero(theme),
    heroLinha: { display: "flex", alignItems: "center", gap: 16 },
    heroAvatar: {
      width: 60,
      height: 60,
      borderRadius: 18,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "1.4rem",
      fontWeight: 800,
      color: VERDE,
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(255,255,255,0.3), 0 8px 24px rgba(0,0,0,0.25)"
    },
    heroEtiqueta: modalHeroEtiqueta,
    heroTitulo: { ...modalHeroTitulo, textTransform: "capitalize" },
    heroChips: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
    heroChip: modalHeroChip,
    fechar: { ...modalFechar, marginLeft: "auto", alignSelf: "flex-start" },
    numeros: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0,1fr))",
      gap: 10,
      marginTop: 18,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0,1fr))" }
    },
    numero: {
      padding: "10px 14px",
      borderRadius: 14,
      background: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.12)",
      backdropFilter: "blur(6px)"
    },
    numeroRotulo: {
      fontSize: "0.64rem",
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: "#94a3b8"
    },
    numeroValor: { fontSize: "1.05rem", fontWeight: 800, color: "#fff", marginTop: 2, whiteSpace: "nowrap" },
    corpo: modalCorpo(theme),
    secao: modalSecao(theme),
    secaoTopo: modalSecaoTopo,
    secaoIcone: modalSecaoIcone,
    secaoTitulo: modalSecaoTitulo(theme),
    grade: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
      gap: "14px 18px"
    },
    rotulo: {
      fontSize: "0.66rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.07em",
      color: theme.palette.text.secondary
    },
    valor: {
      fontSize: "0.9rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
      wordBreak: "break-word",
      marginTop: 2
    },
    valorVazio: { color: theme.palette.text.disabled, fontWeight: 500 },
    oferta: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 12px",
      borderRadius: 12,
      fontSize: "0.8rem",
      fontWeight: 700,
      color: theme.palette.text.primary,
      background: escuro ? "rgba(30,152,85,0.12)" : "#f0fbf4",
      border: "1px solid rgba(30,152,85,0.25)",
      "& b": { color: VERDE }
    },
    link: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 12,
      wordBreak: "break-all",
      fontSize: "0.82rem",
      background: escuro ? "rgba(255,255,255,0.03)" : "#f8fafc",
      border: `1px dashed ${borda}`
    },
    aviso: {
      display: "flex",
      gap: 12,
      padding: "12px 14px",
      borderRadius: 14,
      background: "rgba(229,57,53,0.08)",
      border: "1px solid rgba(229,57,53,0.25)",
      animation: "iaSubir .25s ease backwards",
      "& > svg": { color: "#e53935", flexShrink: 0 }
    },
    rodapeModal: modalRodape(theme),
    btnSecundario: botaoSecundario(theme),
    btnPerigo: botaoPerigo,
    btnPerigoCheio: botaoPerigoCheio
  };
});

const moeda = valor =>
  typeof valor === "number" && valor > 0
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "—";

const cpfFormatado = cpf => {
  const v = String(cpf || "").replace(/\D/g, "");
  if (v.length !== 11) return cpf || "—";
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

const formatarProduto = produto => {
  if (!produto) return "—";
  const p = produto.toLowerCase().trim();
  if (p === "consignado_clt" || p === "clt") return "CLT";
  if (p === "fgts" || p === "saque_aniversario") return "FGTS";
  if (p === "ambos") return "Ambos";
  if (p === "consignado_inss" || p === "inss") return "INSS";
  return produto.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
};

const telefoneFormatado = tel => {
  const v = String(tel || "").replace(/\D/g, "").replace(/^55/, "");
  if (v.length === 11) return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
  if (v.length === 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  return tel || "—";
};

const dataHora = valor => (valor ? new Date(valor).toLocaleString("pt-BR") : "—");

const dataCurta = valor =>
  valor
    ? new Date(valor).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      })
    : "—";

const iniciais = nome => {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return "?";
  return ((partes[0][0] || "") + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
};

const nomeBonito = nome => String(nome || "").toLowerCase();

const corDoStatus = status => {
  const s = String(status || "").toLowerCase();
  if (s.includes("proposta")) return { fundo: "rgba(30,152,85,0.16)", texto: "#1E7A46", ponto: "#22c55e" };
  if (s.includes("escolhida")) return { fundo: "rgba(37,99,235,0.12)", texto: "#1d4ed8", ponto: "#3b82f6" };
  if (s.includes("com oferta")) return { fundo: "rgba(245,158,11,0.14)", texto: "#b45309", ponto: "#f59e0b" };
  return { fundo: "rgba(120,120,120,0.14)", texto: "#64748b", ponto: "#94a3b8" };
};

const BaseClientes = () => {
  const classes = useStyles();

  const [carregando, setCarregando] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);

  const [form, setForm] = useState({ cpf: "", nome: "", telefone: "", banco: "" });
  const [filtros, setFiltros] = useState({ cpf: "", nome: "", telefone: "", banco: "" });

  const [selecionado, setSelecionado] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get("/base-clientes", {
        params: { pagina, porPagina: POR_PAGINA, ...filtros }
      });
      setClientes(data.clientes || []);
      setTotal(data.total || 0);
      setTotalPaginas(data.totalPaginas || 1);
    } catch (err) {
      toastError(err);
    } finally {
      setCarregando(false);
    }
  }, [pagina, filtros]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const aplicarFiltros = () => {
    setPagina(1);
    setFiltros({ ...form });
  };

  const limpar = () => {
    const vazio = { cpf: "", nome: "", telefone: "", banco: "" };
    setForm(vazio);
    setPagina(1);
    setFiltros(vazio);
  };

  const exportar = async () => {
    try {
      const { data } = await api.get("/base-clientes/exportar", {
        params: filtros,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `base-clientes-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const fecharModal = () => {
    setSelecionado(null);
    setConfirmando(false);
  };

  const excluir = async () => {
    if (!selecionado) return;
    setExcluindo(true);
    try {
      await api.delete(`/base-clientes/${selecionado.id}`);
      toast.success("Cliente removido da base.");
      fecharModal();
      if (clientes.length === 1 && pagina > 1) setPagina(p => p - 1);
      else buscar();
    } catch (err) {
      toastError(err);
    } finally {
      setExcluindo(false);
    }
  };

  const aoTeclar = e => {
    if (e.key === "Enter") aplicarFiltros();
  };

  const campo = (nome, rotulo) => (
    <TextField
      className={classes.campo}
      label={rotulo}
      variant="outlined"
      size="small"
      value={form[nome]}
      onChange={e => setForm({ ...form, [nome]: e.target.value })}
      onKeyDown={aoTeclar}
      InputProps={
        nome === "nome"
          ? {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              )
            }
          : undefined
      }
    />
  );

  const temFiltro = Object.values(filtros).some(v => String(v || "").trim());

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <div className={classes.banner}>
          <div>
            <div className={classes.bannerTitulo}>
              <span className={classes.bannerIcone}>
                <StorageOutlinedIcon />
              </span>
              Base de Clientes
            </div>
            <div className={classes.bannerSub}>
              Todos os clientes já consultados pela assistente. Quem volta é atualizado, não duplicado.
            </div>
          </div>

          <div className={classes.bannerDireita}>
            <div className={classes.totalPilula}>
              <div className={classes.totalNumero}>{total.toLocaleString("pt-BR")}</div>
              <div className={classes.totalRotulo}>{temFiltro ? "encontrados" : "clientes na base"}</div>
            </div>
            <Button className={classes.botaoVidro} startIcon={<GetAppIcon />} onClick={exportar} disabled={!total}>
              Exportar planilha
            </Button>
          </div>
        </div>

        <div className={classes.filtros}>
          {campo("nome", "Nome")}
          {campo("cpf", "CPF")}
          {campo("telefone", "Telefone")}
          {campo("banco", "Banco")}
          <Button className={classes.btnBuscar} startIcon={<SearchIcon />} onClick={aplicarFiltros}>
            Buscar
          </Button>
          <Button className={classes.btnLimpar} startIcon={<ClearIcon />} onClick={limpar}>
            Limpar
          </Button>
        </div>

        <div className={classes.listaPainel}>
          <div className={classes.lista}>
            <div className={`${classes.linha} ${classes.cabecalho}`}>
              <span>Cliente</span>
              <span>Nascimento</span>
              <span>Produto / Banco</span>
              <span>Valor / Parcela</span>
              <span>Status</span>
              <span />
            </div>

            {carregando ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={`${classes.linha} ${classes.cartao}`} style={{ cursor: "default" }}>
                  <div className={classes.pessoa}>
                    <Skeleton variant="rect" width={38} height={38} style={{ borderRadius: 12 }} />
                    <div style={{ flex: 1 }}>
                      <Skeleton width="70%" />
                      <Skeleton width="50%" />
                    </div>
                  </div>
                  <Skeleton width="70%" />
                  <Skeleton width="80%" />
                  <Skeleton width="60%" />
                  <Skeleton width="70%" />
                  <span />
                </div>
              ))
            ) : clientes.length === 0 ? (
              <div className={classes.vazio}>
                <StorageOutlinedIcon style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 8 }} />
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  Nenhum cliente encontrado
                </Typography>
                <Typography variant="body2">
                  {temFiltro
                    ? "Tente outro filtro ou clique em Limpar."
                    : "A base é preenchida automaticamente a cada consulta da assistente."}
                </Typography>
              </div>
            ) : (
              clientes.map((c, i) => {
                const cor = corDoStatus(c.statusConsulta);
                return (
                  <div
                    key={c.id}
                    className={`${classes.linha} ${classes.cartao}`}
                    style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}
                    onClick={() => {
                      setConfirmando(false);
                      setSelecionado(c);
                    }}
                  >
                    <div className={classes.pessoa}>
                      <span className={classes.avatar}>{iniciais(c.nome)}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className={classes.nome} title={c.nome}>
                          {c.nome ? nomeBonito(c.nome) : "Sem nome"}
                        </div>
                        <div className={classes.sub}>
                          {cpfFormatado(c.cpf)} · {telefoneFormatado(c.telefone)}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className={classes.forte}>{c.dataNascimento || "—"}</div>
                      <div className={classes.sub}>{c.totalConsultas || 0} consulta(s)</div>
                    </div>

                    <div>
                      <div>
                        <span className={classes.etiquetaProduto}>{formatarProduto(c.produto)}</span>
                        <span className={classes.forte}>{c.bancoEscolhido || "—"}</span>
                      </div>
                      <div className={classes.sub}>Última: {dataCurta(c.ultimaConsultaEm)}</div>
                    </div>

                    <div>
                      <div className={classes.forte}>{moeda(c.valorLiberado)}</div>
                      <div className={classes.sub}>
                        {c.parcela ? `${moeda(c.parcela)}${c.prazo ? ` · ${c.prazo}x` : ""}` : "sem parcela"}
                      </div>
                    </div>

                    <div>
                      {c.statusConsulta ? (
                        <span className={classes.status} style={{ background: cor.fundo, color: cor.texto }}>
                          <span className={classes.ponto} style={{ background: cor.ponto }} />
                          {c.statusConsulta}
                        </span>
                      ) : (
                        <span className={classes.sub}>—</span>
                      )}
                    </div>

                    <ChevronRightIcon className={classes.seta} />
                  </div>
                );
              })
            )}
          </div>

          <div className={classes.rodape}>
            <Typography variant="body2" color="textSecondary">
              {total === 0 ? "Nenhum cliente" : `${total} cliente(s) — página ${pagina} de ${totalPaginas}`}
            </Typography>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Tooltip title="Página anterior" arrow>
                <span>
                  <IconButton
                    size="small"
                    className={classes.paginaBtn}
                    disabled={pagina <= 1 || carregando}
                    onClick={() => setPagina(p => Math.max(1, p - 1))}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Próxima página" arrow>
                <span>
                  <IconButton
                    size="small"
                    className={classes.paginaBtn}
                    disabled={pagina >= totalPaginas || carregando}
                    onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>

        <DetalheCliente
          cliente={selecionado}
          classes={classes}
          confirmando={confirmando}
          excluindo={excluindo}
          onPedirExclusao={() => setConfirmando(true)}
          onCancelarExclusao={() => setConfirmando(false)}
          onExcluir={excluir}
          onFechar={fecharModal}
        />
      </Container>
    </div>
  );
};

const Dado = ({ classes, rotulo, valor }) => {
  const vazio = valor === null || valor === undefined || valor === "" || valor === "—";
  return (
    <div>
      <div className={classes.rotulo}>{rotulo}</div>
      <div className={`${classes.valor} ${vazio ? classes.valorVazio : ""}`}>{vazio ? "—" : valor}</div>
    </div>
  );
};

const Secao = ({ classes, icone, titulo, children, atraso = 0 }) => (
  <div className={classes.secao} style={{ animationDelay: `${atraso}ms` }}>
    <div className={classes.secaoTopo}>
      <span className={classes.secaoIcone}>{icone}</span>
      <span className={classes.secaoTitulo}>{titulo}</span>
    </div>
    {children}
  </div>
);

const DetalheCliente = ({
  cliente,
  classes,
  confirmando,
  excluindo,
  onPedirExclusao,
  onCancelarExclusao,
  onExcluir,
  onFechar
}) => {
  if (!cliente) return null;

  let ofertas = [];
  try {
    ofertas = cliente.bancosComOferta ? JSON.parse(cliente.bancosComOferta) : [];
  } catch (e) {
    ofertas = [];
  }

  const cor = corDoStatus(cliente.statusConsulta);

  return (
    <Dialog open onClose={onFechar} maxWidth={false} PaperProps={{ className: classes.papel }}>
      <div className={classes.hero}>
        <div className={classes.heroLinha}>
          <div className={classes.heroAvatar}>{iniciais(cliente.nome)}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className={classes.heroEtiqueta}>Base de clientes</div>
            <div className={classes.heroTitulo}>
              {cliente.nome ? nomeBonito(cliente.nome) : "Cliente sem nome"}
            </div>
            <div className={classes.heroChips}>
              <span className={classes.heroChip}>
                <CreditCardIcon /> {cpfFormatado(cliente.cpf)}
              </span>
              <span className={classes.heroChip}>
                <PhoneIphoneIcon /> {telefoneFormatado(cliente.telefone)}
              </span>
              {cliente.statusConsulta && (
                <span className={classes.heroChip}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: cor.ponto }} />
                  {cliente.statusConsulta}
                </span>
              )}
            </div>
          </div>
          <IconButton size="small" onClick={onFechar} className={classes.fechar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div className={classes.numeros}>
          {[
            ["Valor liberado", moeda(cliente.valorLiberado)],
            ["Parcela", moeda(cliente.parcela)],
            ["Prazo", cliente.prazo ? `${cliente.prazo}x` : "—"],
            ["Consultas", cliente.totalConsultas || 0]
          ].map(([r, v]) => (
            <div key={r} className={classes.numero}>
              <div className={classes.numeroRotulo}>{r}</div>
              <div className={classes.numeroValor}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={classes.corpo}>
        <Secao classes={classes} icone={<PersonOutlineIcon />} titulo="Dados pessoais">
          <div className={classes.grade}>
            <Dado classes={classes} rotulo="Nome completo" valor={cliente.nome} />
            <Dado classes={classes} rotulo="CPF" valor={cpfFormatado(cliente.cpf)} />
            <Dado classes={classes} rotulo="Nascimento" valor={cliente.dataNascimento} />
            <Dado classes={classes} rotulo="Sexo" valor={cliente.sexo} />
            <Dado classes={classes} rotulo="Nome da mãe" valor={cliente.nomeMae} />
            <Dado classes={classes} rotulo="Telefone" valor={telefoneFormatado(cliente.telefone)} />
            <Dado classes={classes} rotulo="Ocupação" valor={cliente.ocupacao} />
            <Dado classes={classes} rotulo="Renda" valor={moeda(cliente.renda)} />
          </div>
        </Secao>

        <Secao classes={classes} icone={<RoomOutlinedIcon />} titulo="Endereço" atraso={50}>
          <div className={classes.grade}>
            <Dado classes={classes} rotulo="Logradouro" valor={cliente.endereco} />
            <Dado classes={classes} rotulo="Bairro" valor={cliente.bairro} />
            <Dado classes={classes} rotulo="Cidade" valor={cliente.cidade} />
            <Dado classes={classes} rotulo="UF" valor={cliente.uf} />
            <Dado classes={classes} rotulo="CEP" valor={cliente.cep} />
          </div>
        </Secao>

        <Secao classes={classes} icone={<AccountBalanceOutlinedIcon />} titulo="Condição atual" atraso={100}>
          <div className={classes.grade}>
            <Dado classes={classes} rotulo="Produto" valor={formatarProduto(cliente.produto)} />
            <Dado classes={classes} rotulo="Banco" valor={cliente.bancoEscolhido} />
            <Dado classes={classes} rotulo="Valor liberado" valor={moeda(cliente.valorLiberado)} />
            <Dado classes={classes} rotulo="Parcela" valor={moeda(cliente.parcela)} />
            <Dado classes={classes} rotulo="Prazo" valor={cliente.prazo ? `${cliente.prazo}x` : ""} />
            <Dado classes={classes} rotulo="Status" valor={cliente.statusConsulta} />
          </div>

          {ofertas.length > 0 && (
            <>
              <div className={classes.rotulo} style={{ margin: "16px 0 8px" }}>
                Bancos com oferta na última consulta
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {ofertas.map((o, i) => (
                  <span key={`${o.banco}-${i}`} className={classes.oferta}>
                    {o.banco}
                    {o.valor ? <b>{moeda(Number(o.valor))}</b> : null}
                  </span>
                ))}
              </div>
            </>
          )}

          {cliente.linkFormalizacao && (
            <>
              <div className={classes.rotulo} style={{ margin: "16px 0 8px" }}>
                Link de formalização
              </div>
              <div className={classes.link}>
                <LinkIcon style={{ color: VERDE, fontSize: 18, flexShrink: 0 }} />
                <Link href={cliente.linkFormalizacao} target="_blank" rel="noopener noreferrer">
                  {cliente.linkFormalizacao}
                </Link>
              </div>
            </>
          )}
        </Secao>

        <Secao classes={classes} icone={<HistoryIcon />} titulo="Histórico" atraso={150}>
          <div className={classes.grade}>
            <Dado classes={classes} rotulo="Consultas" valor={cliente.totalConsultas || 0} />
            <Dado classes={classes} rotulo="Última consulta" valor={dataHora(cliente.ultimaConsultaEm)} />
            <Dado classes={classes} rotulo="Primeira vez" valor={dataHora(cliente.createdAt)} />
            <Dado
              classes={classes}
              rotulo="Último atendimento"
              valor={cliente.ultimoTicketId ? `#${cliente.ultimoTicketId}` : ""}
            />
          </div>
        </Secao>

        {confirmando && (
          <div className={classes.aviso}>
            <ErrorOutlineIcon />
            <div>
              <Typography variant="subtitle2" style={{ fontWeight: 800 }}>
                Excluir {cliente.nome ? nomeBonito(cliente.nome) : "este cliente"} da base?
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Some só daqui. As conversas, o contato no WhatsApp e os atendimentos continuam intactos.
                Se a assistente consultar este CPF de novo, ele volta para a lista.
              </Typography>
            </div>
          </div>
        )}
      </div>

      <div className={classes.rodapeModal}>
        {confirmando ? (
          <>
            <div style={{ flex: 1 }} />
            <Button onClick={onCancelarExclusao} disabled={excluindo} className={classes.btnSecundario}>
              Cancelar
            </Button>
            <Button
              onClick={onExcluir}
              disabled={excluindo}
              className={classes.btnPerigoCheio}
              startIcon={excluindo ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            >
              {excluindo ? "Excluindo..." : "Excluir definitivamente"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onPedirExclusao} startIcon={<DeleteOutlineIcon />} className={classes.btnPerigo}>
              Excluir da base
            </Button>
            <div style={{ flex: 1 }} />
            <Button onClick={onFechar} className={classes.btnSecundario}>
              Fechar
            </Button>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default BaseClientes;
